<template>
  <v-container fluid class="pa-4 pa-sm-6">
    <!-- ВЕРХНЯЯ ПАНЕЛЬ: Переключатель недель -->
     <PageTitle title="Расписание" />

    <v-row class="align-center mb-4" no-gutters>
      <v-col cols="12" sm="7" class="d-flex align-center mb-3 mb-sm-0">
        <v-btn icon="mdi-chevron-left" variant="outlined" density="comfortable" @click="navigateWeek(-1)"></v-btn>

        <v-btn variant="text" class="mx-1 font-weight-bold text-body-1" @click="setTodayWeek">
          {{ formattedWeekRange }}
        </v-btn>

        <v-btn icon="mdi-chevron-right" variant="outlined" density="comfortable" @click="navigateWeek(1)"></v-btn>

        <v-btn v-if="!isCurrentWeekSelected" size="small" variant="tonal" color="secondary" class="ml-2" @click="setTodayWeek">
          Текущая неделя
        </v-btn>
      </v-col>

      <v-col cols="12" sm="5" class="text-sm-right" v-if="isAdminOrTeacher">
        <v-btn  color="deep-orange-darken-1" size="large" class="font-weight-bold px-6" prepend-icon="mdi-calendar-plus" @click="openCreateDialog">
          Запланировать
        </v-btn>
      </v-col>
    </v-row>

    <!-- ИНДИКАТОР ЗАГРУЗКИ СЕТКИ -->
    <v-progress-linear v-if="isScheduleLoading" indeterminate color="primary" class="mb-4 rounded"></v-progress-linear>

    <!-- ОСНОВНАЯ СЕТКА ДНЕЙ НЕДЕЛИ -->
    <v-row v-if="!isScheduleLoading">
      <v-col v-for="day in weekDays" :key="day.dateString" cols="12" md="6" lg="4" xl="3" class="d-flex">
        <v-card :class="['rounded-lg flex-grow-1 border-top-thick d-flex flex-column', isToday(day.dateString) ? 'border-primary elevation-3' : 'elevation-1 border']" variant="flat">

          <!-- Шапка дня недели -->
         <template v-slot:title>
           <div class="d-flex align-center w-100">
             <!-- Название слева -->
             <span class="font-weight-black"  :class="isToday(day.dateString) ? 'text-deep-orange-darken-1' : 'text-grey-darken-1'">
               {{ day.dayName }}
             </span>

             <!-- Расталкиватель посередине -->
             <v-spacer></v-spacer>

             <!-- Чип справа -->
             <v-chip
               :color="isToday(day.dateString) ? 'deep-orange-darken-1' : 'grey-darken-1'"
               size="small"
               variant="flat"
               class="font-weight-bold"
             >
               {{ day.displayDate }}
             </v-chip>
           </div>
         </template>

          <!-- Лента карточек событий дня -->
          <v-card-text class="pa-2 overflow-y-auto flex-grow-1" style="max-height: 550px; min-height: 120px;">
            <div v-if="getDayEvents(day.dateString).length === 0" class="text-center text-grey py-10 text-caption font-italic">
              Нет запланированных событий
            </div>

            <v-card
              v-for="event in getDayEvents(day.dateString)"
              :key="event.id"
              class="mb-2 pa-3 rounded-lg border-s-lg elevation-0 bg-grey-lighten-5 border position-relative"
              :style="{ borderColor: getEventColor(event.eventType) + ' !important' }"
            >

              <!-- Кнопка удаления для админа -->
              <v-btn v-if="isAdminOrTeacher" icon="mdi-close" size="x-small" variant="text" color="error" class="position-absolute-top-right" @click="confirmDeleteEvent(event.id)"></v-btn>

              <!-- Метки типа и времени -->
              <div class="d-flex justify-space-between align-center mb-2 pr-6">
                <v-chip variant="outlined" :color="getEventColor(event.eventType)" size="x-small" class="text-white font-weight-bold text-uppercase px-2">
                  {{ getEventTypeText(event.eventType) }}
                </v-chip>
                <span class="text-caption font-weight-bold text-grey-darken-3">
                  <v-icon size="x-small" class="mr-1">mdi-clock-outline</v-icon>
                  {{ formatTime(event.startTime) }}{{ event.endTime ? ' - ' + formatTime(event.endTime) : '' }}
                </span>
              </div>

              <div class="text-body-2 font-weight-bold text-grey-darken-4 mb-1">{{ event.title }}</div>
              <div v-if="event.description" class="text-caption text-grey-darken-2 mb-2 whitespace-pre-line">{{ event.description }}</div>

              <!-- Команды -->
              <div class="mb-1" v-if="event.targetGroups && event.targetGroups.length > 0">
                <h6>Команды:</h6>
                <v-chip v-for="team in event.targetGroups" :key="team.id" size="x-small" variant="tonal" color="deep-orange-darken-3" class="mr-1 mb-1">
                  {{ team.personGroupName }}
                </v-chip>
              </div>

              <!-- Вебинарная ссылка -->
              <div v-if="event.eventType === 'WEBINAR' && event.webinarLink" class="bg-blue-lighten-5 pa-2 rounded-lg mt-2 border border-blue-lighten-4">
                <div class="d-flex align-center justify-space-between mb-1">
                  <span class="text-blue-darken-4 font-weight-bold text-caption">Вебинар:</span>
                  <v-btn :href="event.webinarLink" target="_blank" size="x-small" color="blue-darken-1" variant="flat" prepend-icon="mdi-video">Войти</v-btn>
                </div>
                <div v-if="event.accessCode" class="text-caption text-grey-darken-3">Код: <code>{{ event.accessCode }}</code></div>
              </div>
                 <!-- [ДОБАВИТЬ ЭТОТ БЛОК] Блок прикрепленных методических материалов (PDF, DOCX) -->
                              <div v-if="event.attachedMaterials && event.attachedMaterials.length > 0" class="border-t pt-2 mt-2">
                                <div class="text-caption font-weight-bold text-grey-darken-3 mb-1">
                                  <v-icon size="x-small" class="mr-1">mdi-paperclip</v-icon>
                                  Методические материалы:
                                </div>

                                <!-- Рендерим только документы (пропускаем видео, так как для них есть отдельная кнопка медиатеки) -->
                                <div
                                  v-for="file in event.attachedMaterials"
                                  :key="file.id"
                                  class="d-flex align-center text-caption text-primary cursor-pointer mb-1 hover-underline"
                                  @click="downloadSecureFile(file.id, file.fileName)"
                                  title="Нажмите, чтобы скачать документ"
                                >
                                  <!-- Динамическая иконка в зависимости от расширения файла -->
                                  <v-icon size="x-small" :color="getFileIconColor(file.fileName)" class="mr-1">
                                    {{ getFileIcon(file.fileName) }}
                                  </v-icon>
                                  <span class="text-decoration-underline text-truncate-1 text-grey-darken-4">
                                    {{ file.fileName }}
                                  </span>
                                </div>
                              </div>


                           <!-- Блок прикрепленных видеоуроков (HLS-видео) -->
                                        <div v-if="hasVideoMaterials(event.attachedMaterials)" class="border-t pt-2 mt-2">
                                          <div class="text-caption font-weight-bold text-grey-darken-3 mb-1">
                                            <v-icon size="x-small" color="red" class="mr-1">mdi-play-circle</v-icon>
                                            Видеоматериалы к занятию:
                                          </div>

                                          <!-- Кнопка запуска вашей медиатеки с плеером -->
                                          <v-btn
                                            variant="flat"
                                            color="indigo-darken-4"
                                            size="x-small"
                                            block
                                            prepend-icon="mdi-movie-play-outline"
                                            class="font-weight-bold text-white rounded-md mt-1"
                                            @click="openMediaForEvent(event)"
                                          >
                                            Смотреть видеоуроки
                                          </v-btn>
                                        </div>
            </v-card>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
 <!-- МОДАЛЬНОЕ ОКНО СОЗДАНИЯ (Интерфейс Администратора/Преподавателя) -->
    <CreateEventDialog
      v-model="createDialog"
      :default-date="currentMonday.toISOString().split('T')[0]"
      @saved="fetchWeeklySchedule"
    />
    <MediaViewerDialog
      v-model="mediaDialog"
      :active-module="activeModule"
      :video-files="videoFiles"
      :download-animation="downloadAnimation"
      :upload-percentage="uploadPercentage"
      :available-levels="availableLevels"
      :current-level-index="currentLevelIndex"
      @init-player="initHlsPlayer"
      @change-quality="changeVideoQuality"
      @close="closeMediaModal"
    />
  </v-container>
</template>
<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import PageTitle from "@/elements/PageTitle.vue";
import { customAxios } from "@/service/customAxios";
import CreateEventDialog from '@/schedule/CreateEventDialog.vue'
import MediaViewerDialog from '@/schedule/MediaViewerDialog.vue'
// Ролевая модель (в реальном приложении получается из Pinia/Vuex/Сессии)
const userRole = ref('ADMIN') // Возможные значения: ADMIN, TEACHER, STUDENT
const studentTeamId = ref(5)     // ID команды студента

// Состояния загрузок и модальных окон
const isScheduleLoading = ref(false)

const isTeamsLoading = ref(false)
const createDialog = ref(false)
// Переменные состояния для медиа-диалога
const mediaDialog = ref(false)
const activeModule = ref(null)
const videoFiles = ref([])

// --- Переменные для интеграции с MediaViewerDialog ---




// НЕДОСТАЮЩИЕ РЕАКТИВНЫЕ ПЕРЕМЕННЫЕ (Добавьте их):
const downloadAnimation = ref(false)
const uploadPercentage = ref(0)
const availableLevels = ref([])
const currentLevelIndex = ref(-1)

// Функции управления медиаплеером и загрузкой видео




// НЕДОСТАЮЩИЕ ФУНКЦИИ (Добавьте их):
function initHlsPlayer(videoElement, streamUrl) {
  // Если у вас есть глобальная функция или импортированный метод,
  // вызовите его здесь. Пока оставляем базовую проверку для гашения варнинга:
  console.log("Инициализация плеера из ScheduleTable для:", streamUrl)
}

function changeVideoQuality(levelIndex) {
  currentLevelIndex.value = levelIndex
  console.log("Переключение качества на уровень:", levelIndex)
}



// Массивы данных из БД
const events = ref([])
const availableTeams = ref([])

// Смещение недель относительно текущей (0 — текущая, -1 — прошлая, 1 — следующая)
const weekOffset = ref(0)



// Права текущего пользователя на добавление и удаление
const isAdminOrTeacher = computed(() => {
  return userRole.value === 'ADMIN' || userRole.value === 'TEACHER'
})

// Календарная логика: Вычисление даты Понедельника выбранной недели
const currentMonday = computed(() => {
  const today = new Date()
  const dayOfWeek = today.getDay() // 0 (вс) - 6 (сб)
  const dayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek // Сдвиг к понедельнику

  const monday = new Date(today)
  monday.setDate(today.getDate() + dayOffset + (weekOffset.value * 7))
  monday.setHours(0, 0, 0, 0)
  return monday
})

// Проверка: Выбрана ли текущая календарная неделя
const isCurrentWeekSelected = computed(() => {
  return weekOffset.value === 0
})

// Текст диапазона дат в селекторе недель (например: "24 авг — 30 авг 2026")
const formattedWeekRange = computed(() => {
  const start = new Date(currentMonday.value)
  const end = new Date(currentMonday.value)
  end.setDate(start.getDate() + 6)

  const options = { month: 'short', day: 'numeric' }
  const startText = start.toLocaleDateString('ru-RU', options)
  const endText = end.toLocaleDateString('ru-RU', options)
  const year = end.getFullYear()

  return `${startText} — ${endText} ${year}`
})

// Генерация массива из 7 дней для рендера адаптивных колонок
const weekDays = computed(() => {
  const days = []
  const names = ['Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота', 'Воскресенье']

  for (let i = 0; i < 7; i++) {
    const nextDay = new Date(currentMonday.value)
    nextDay.setDate(currentMonday.value.getDate() + i)

    // YYYY-MM-DD для фильтрации и сопоставления с бэкендом
    const dateString = nextDay.toISOString().split('T')[0]

    // DD.MM для красивого чипа в шапке
    const displayDate = nextDay.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })

    days.push({
      dayName: names[i],
      displayDate,
      dateString
    })
  }
  return days
})

// Функции управления селектором недель
const navigateWeek = (direction) => {
  weekOffset.value += direction
}

const setTodayWeek = () => {
  weekOffset.value = 0
}

const isToday = (dateString) => {
  // Получаем текущую локальную дату строго в формате YYYY-MM-DD
  const todayStr = new Date().toLocaleDateString('sv') // Вернет "2026-08-22"

  // Прямое сравнение двух строк "2026-08-22" === "2026-08-22"
  return dateString === todayStr
}

// Объект формы для создания нового события
const defaultEvent = {
  title: '',
  description: '',
  eventType: 'LESSON',
  date: '',
  startTime: '',
  endTime: '',
  webinarLink: '',
  accessCode: '',
  targetTeamIds: []
}
const newEvent = ref({ ...defaultEvent })

// 📥 Загрузка массива событий на неделю с бэкенда
const fetchWeeklySchedule = async () => {
  isScheduleLoading.value = true
  try {
    const startDate = currentMonday.value.toISOString().split('T')[0]
    const end = new Date(currentMonday.value)
    end.setDate(end.getDate() + 6)
    const endDate = end.toISOString().split('T')[0]

    const params = {
      startDate,
      endDate,
      userRole: userRole.value
    }

    if (userRole.value === 'STUDENT') {
      params.teamId = studentTeamId.value
    }

    const response = await customAxios.get('/v1/schedule', { params })
    events.value = response.data
  } catch (e) {
    console.error('Ошибка при загрузке расписания:', e)
  } finally {
    isScheduleLoading.value = false
  }
}

// 📥 Загрузка списка доступных команд (для формы админа)
const fetchAvailableTeams = async () => {
  if (!isAdminOrTeacher.value) return
  isTeamsLoading.value = true
  try {
    const response = await customAxios.get('/user_group') // Предполагаемый ваш эндпоинт команд
    availableTeams.value = response.data
  } catch (e) {
    console.error('Ошибка загрузки команд:', e)
  } finally {
    isTeamsLoading.value = false
  }
}

// Фильтрация событий для конкретного дня недели на фронтенде
const getDayEvents = (dateString) => {
  return events.value.filter(event => {
    if (!event.startTime) return false
    return event.startTime.split('T')[0] === dateString
  })
}

// Форматирование времени (из ISO строки "2026-08-24T14:30:00" в "14:30")
const formatTime = (dateTimeStr) => {
  if (!dateTimeStr) return ''
  const parts = dateTimeStr.split('T')
  if (parts.length < 2) return ''
  return parts[1].substring(0, 5)
}

// Цвета для разных типов событий
const getEventColor = (type) => {
  switch (type) {
    case 'LESSON': return 'primary'     // Синий
    case 'WEBINAR': return 'secondary'    // Красный
    case 'TASK': return 'red'       // Оранжевый
    case 'SELF_STUDY': return 'green' // Зеленый
    default: return 'primary'
  }
}

const getEventTypeText = (type) => {
  switch (type) {
    case 'LESSON': return 'Занятие'
    case 'WEBINAR': return 'Вебинар Zoom'
    case 'TASK': return 'Дедлайн'
    case 'SELF_STUDY': return 'Сам. работа'
    default: return type
  }
}

// Управление модальным окном
const openCreateDialog = () => {
  newEvent.value = { ...defaultEvent, date: currentMonday.value.toISOString().split('T')[0] }
  createDialog.value = true
  fetchAvailableTeams()
}



// 🗑 Удаление события (DELETE /api/v1/schedule/{id})
const confirmDeleteEvent = async (eventId) => {
  if (!confirm('Удалить это событие из расписания?')) return
  try {
    await customAxios.delete(`/v1/schedule/${eventId}`)
    await fetchWeeklySchedule()
  } catch (e) {
    console.error('Ошибка при удалении:', e)
  }
}

// Функция для безопасного скачивания файла по его ID из БД через защищенный эндпоинт
const downloadSecureFile = async (fileId, originalFileName) => {
  try {
    // Делаем асинхронный запрос с типом ответа 'blob' (бинарные данные)
    const response = await customAxios.get(`/v1/files/download/${fileId}`, {
      responseType: 'blob'
    })

    // Создаем временную ссылку в памяти браузера на полученный бинарный объект
    const blob = new Blob([response.data], { type: response.headers['content-type'] })
    const downloadUrl = window.URL.createObjectURL(blob)

    // Создаем невидимый элемент ссылки, имитируем клик для скачивания и очищаем DOM
    const link = document.createElement('a')
    link.href = downloadUrl
    link.setAttribute('download', originalFileName || 'document')
    document.body.appendChild(link)
    link.click()

    // Освобождаем ресурсы памяти
    link.remove()
    window.URL.revokeObjectURL(downloadUrl)
  } catch (error) {
    console.error('Не удалось скачать файл. Возможно, доступ ограничен или файл удален с сервера:', error)
    alert('Ошибка при скачивании файла. Пожалуйста, обратитесь к администратору.')
  }
}

// Вспомогательные хелперы для иконок документов в списке расписания
const getFileIcon = (fileName) => {
  if (!fileName) return 'mdi-file-document-outline'
  const ext = fileName.split('.').pop()?.toLowerCase()
  switch (ext) {
    case 'pdf': return 'mdi-file-pdf-box'
    case 'doc':
    case 'docx': return 'mdi-file-word-box'
    case 'xls':
    case 'xlsx': return 'mdi-file-excel-box'
    case 'ppt':
    case 'pptx': return 'mdi-file-powerpoint-box'
    default: return 'mdi-file-document-outline'
  }
}

const getFileIconColor = (fileName) => {
  if (!fileName) return 'grey'
  const ext = fileName.split('.').pop()?.toLowerCase()
  switch (ext) {
    case 'pdf': return 'red-darken-1'
    case 'doc':
    case 'docx': return 'blue-darken-2'
    case 'xls':
    case 'xlsx': return 'green-darken-2'
    case 'ppt':
    case 'pptx': return 'orange-darken-2'
    default: return 'grey-darken-2'
  }
}

// Функция проверяет, есть ли среди прикрепленных материалов видеофайлы (например, по наличию поля sourceHlsIndex)
const hasVideoMaterials = (materials) => {
  if (!materials || materials.length === 0) return false
  return materials.some(mat => mat.sourceHlsIndex || mat.posterName)
}

// Открытие модального окна просмотра видео для конкретного занятия
const openMediaForEvent = (event) => {
  // Фильтруем и вытаскиваем только видеофайлы из общего списка материалов занятия
  const eventVideos = event.attachedMaterials.filter(mat => mat.sourceHlsIndex || mat.posterName)

  // Вызываем функцию открытия, передавая объект и массив его видеороликов
  openVideoDialog({
    id: event.id,
    courseModuleName: event.title,
    videoFiles: eventVideos
  })
}

function openVideoDialog(mod) {
  activeModule.value = mod
  videoFiles.value = mod.videoFiles
  mediaDialog.value = true
}

function closeMediaModal() {
  mediaDialog.value = false
  activeModule.value = null
}

// Автоматический перезапрос данных при переключении недель кнопками селектора
watch(weekOffset, () => {
  fetchWeeklySchedule()
})

onMounted(() => {
  fetchWeeklySchedule()
})
</script>

<style scoped>
/* Толстая верхняя граница для карточек дней */
.border-top-thick {
  border-top: 4px solid !important;
}

/* Абсолютное позиционирование для кнопки удаления в карточке занятия */
.position-absolute-top-right {
  position: absolute;
  top: 4px;
  right: 4px;
  z-index: 2;
}

.whitespace-pre-line {
  white-space: pre-line;
}

.hover-underline:hover span {
  color: var(--v-theme-primary) !important;
  text-decoration: underline;
}

.text-truncate-1 {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 90%;
}
</style>

